import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2, Edit, Pin, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const priorityColors = {
  normal: "bg-gray-100 text-gray-600",
  important: "bg-amber-50 text-amber-700",
  urgent: "bg-red-50 text-red-700",
};

const audienceLabels = {
  all: "Everyone",
  clients: "Clients",
  volunteers: "Volunteers",
  staff: "Staff",
};

export const AnnouncementsPanel = ({ getAuthHeader }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: "", content: "", audience: "all", priority: "normal", pinned: false, expires_at: "",
  });

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/announcements`, getAuthHeader());
      setAnnouncements(res.data);
      setLoaded(true);
    } catch { toast.error("Failed to load announcements"); }
  };

  if (!loaded) fetchData();

  const resetForm = () => {
    setForm({ title: "", content: "", audience: "all", priority: "normal", pinned: false, expires_at: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/announcements/${editingId}`, form, getAuthHeader());
        toast.success("Announcement updated");
      } else {
        await axios.post(`${API}/admin/announcements`, form, getAuthHeader());
        toast.success("Announcement posted");
      }
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (a) => {
    setForm({
      title: a.title, content: a.content, audience: a.audience,
      priority: a.priority, pinned: a.pinned || false, expires_at: a.expires_at || "",
    });
    setEditingId(a.id);
    setShowForm(true);
  };

  const handleToggleActive = async (a) => {
    try {
      await axios.put(`${API}/admin/announcements/${a.id}`, { is_active: !a.is_active }, getAuthHeader());
      toast.success(a.is_active ? "Hidden from users" : "Now visible to users");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const handleTogglePin = async (a) => {
    try {
      await axios.put(`${API}/admin/announcements/${a.id}`, { pinned: !a.pinned }, getAuthHeader());
      toast.success(a.pinned ? "Unpinned" : "Pinned to top");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, getAuthHeader());
      toast.success("Deleted");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const active = announcements.filter(a => a.is_active).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-gray-500">{announcements.length} total, {active} active</p>
        <Button data-testid="add-announcement-btn" onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
          <Plus className="w-4 h-4" />{showForm ? "Cancel" : "New Announcement"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-5 space-y-4">
          <div>
            <Label className="text-sm font-medium text-gray-700">Title *</Label>
            <Input data-testid="announcement-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Office Closed for Holiday" className="mt-1" />
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Message *</Label>
            <textarea
              data-testid="announcement-content"
              value={form.content}
              onChange={e => setForm({ ...form, content: e.target.value })}
              required
              rows={4}
              placeholder="Write your announcement here..."
              className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-warm-500 focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Audience</Label>
              <select data-testid="announcement-audience" value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
                <option value="all">Everyone</option>
                <option value="clients">Clients Only</option>
                <option value="volunteers">Volunteers Only</option>
                <option value="staff">Staff Only</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Priority</Label>
              <select data-testid="announcement-priority" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm">
                <option value="normal">Normal</option>
                <option value="important">Important</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Expires On</Label>
              <Input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.pinned} onChange={e => setForm({ ...form, pinned: e.target.checked })} className="rounded" />
              <span className="text-sm text-gray-700">Pin to top</span>
            </label>
          </div>
          <Button type="submit" data-testid="announcement-submit" className="bg-warm-700 hover:bg-warm-800 text-white">
            {editingId ? "Update" : "Post"} Announcement
          </Button>
        </form>
      )}

      <div className="space-y-3">
        {announcements.map(a => (
          <div key={a.id} className={`bg-white border rounded-xl px-5 py-4 transition-all ${a.is_active ? "border-gray-100" : "border-gray-100 opacity-50"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {a.pinned && <Pin className="w-3.5 h-3.5 text-warm-600" />}
                  <span className="font-medium text-gray-900">{a.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColors[a.priority]}`}>{a.priority}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-warm-50 text-warm-700 font-medium">{audienceLabels[a.audience]}</span>
                  {!a.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Hidden</span>}
                </div>
                <p className="text-sm text-gray-600 whitespace-pre-line line-clamp-3">{a.content}</p>
                <p className="text-xs text-gray-400 mt-2">
                  Posted {new Date(a.created_at).toLocaleDateString()}
                  {a.expires_at ? ` | Expires ${a.expires_at}` : ""}
                  {a.posted_by ? ` | By ${a.posted_by}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => handleTogglePin(a)} className="h-8 w-8 p-0" title={a.pinned ? "Unpin" : "Pin"}>
                  <Pin className={`w-4 h-4 ${a.pinned ? "text-warm-600" : "text-gray-400"}`} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleToggleActive(a)} className="h-8 w-8 p-0" title={a.is_active ? "Hide" : "Show"}>
                  {a.is_active ? <Eye className="w-4 h-4 text-emerald-500" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleEdit(a)} className="h-8 w-8 p-0"><Edit className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="h-8 w-8 p-0 text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
        {announcements.length === 0 && (
          <div className="py-12 text-center text-gray-400 text-sm">
            No announcements yet. Post one to keep your team informed.
          </div>
        )}
      </div>
    </div>
  );
};
